import { ApiClient, ApiResult } from "./ApiClient";
import MessageProvider from "./MessageProvider";
import { Location, InitialConfigurationData, LocationResultSet, HotelResultSet, BookingSession, BookingSessionCreateParams, RoomTypeResult, TemporaryRoomTypeSelection, GuestInfo, 
    BookerInfo, HotelDetail, BookingLookupOTP, PaymentOption, TentativeBookingResult, AuthResponse, HotelDetailsByAgent, PaymentIntentCreateResponse, CheckConfirmation, 
    BookingDetail, PaymentConfirmation, Article,
    HotelDetailByAgent,
    Amenity,
    UserAccountResultSet,
    OtpResultSet,
    UserAccountRequest,
    UserAccountDetails,
    SearchCriteriaAndHotelResultSet} from "./Models";
import HotelSearchCriteria from "./states/HotelSearchCriteria";
import RoomSearchCriteria from "./states/RoomSearchCriteria";
import BookingSearchCriteria from "./states/BookingSearchCriteria";
import HotelLookupCriteria from "./states/HotelLookupCriteria";
//import { UUID } from "crypto";

interface InitialConfigurationDataCacheObject {
    initialConfigData: InitialConfigurationData,
    timestamp: number
}

interface CachedObjectWrapper {
    object: any,
    timestamp: number,
    expiringInSeconds: number
}

class LocalObjectCache {
    getObject(key:string): any|null {
        let storedContent = localStorage.getItem(key);
        if(storedContent != null) {
            console.log("Retrieving object from the local store where key = ", key);
            const cachedObjectWrapper:CachedObjectWrapper = JSON.parse(storedContent) as CachedObjectWrapper;
            const currentTimestamp = new Date().getTime();
            const miliseondsInOneSecond = 1000; 
            const secondsSinceSaved = (currentTimestamp - cachedObjectWrapper.timestamp) / miliseondsInOneSecond;
            console.log("Object was cached ", secondsSinceSaved, "seconds ago"); 
            if(secondsSinceSaved < cachedObjectWrapper.expiringInSeconds) {
                return cachedObjectWrapper.object;
            } else {
                console.log("Object cache is ignored since it is over ", cachedObjectWrapper.expiringInSeconds, "seconds");    
                return null;
            } 
        }  
    }

    setObject(key:string, object:any, expiringInSeconds: number) {
        let cachedObjectWrapper:CachedObjectWrapper = {
            object: object,
            timestamp: new Date().getTime(),
            expiringInSeconds: expiringInSeconds
        }
        localStorage.setItem(key, JSON.stringify(cachedObjectWrapper));
    }
}

export default class DataSource {
    apiClient: ApiClient
    messageProvider: MessageProvider
    localObjectCache: LocalObjectCache

    constructor(apiClient: ApiClient, messageProvider: MessageProvider) {
        this.apiClient = apiClient;
        this.messageProvider = messageProvider;
        this.localObjectCache = new LocalObjectCache();
    }

    authentication(agentId:number, channel:string, appUserToken:string): Promise<AuthResponse>{
        return this.apiClient.authentication(agentId, channel, appUserToken).then(result => {
            return result;
        })
    }

    getUserAccountByWalletUserId(walletUserId: string): Promise<UserAccountResultSet>{
        return this.apiClient.getUserAccountByWalletUserId(walletUserId);
    }

    createUserAccountWithPhoneNumber(userAccountRequest: UserAccountRequest): Promise<ApiResult<UserAccountResultSet>> {
        return this.apiClient.createUserAccountWithPhoneNumber(userAccountRequest).then(result => {
            return result;
        })
    }

    getOtp(phoneNumber: string): Promise<ApiResult<OtpResultSet>> {
        return this.apiClient.getOtp(phoneNumber).then(result => {
            return result;
        })
    }

    getUserAccountDetails(userId: string): Promise<ApiResult<UserAccountDetails>> {
        return this.apiClient.getUserAccountDetails(userId).then(result => {
            return result;
        })
    }

    fetchInitialConfigurationData(auth: AuthResponse) : Promise<InitialConfigurationData> {
        this.apiClient.setAuthorization(auth.accessToken);
        this.apiClient.setWalletUserId(auth.walletUserId);
        let storedContent = this.localObjectCache.getObject("initial-config-data")
        if(storedContent) {
            const initialConfigDataCacheObject = storedContent as InitialConfigurationDataCacheObject;
            return Promise.resolve(initialConfigDataCacheObject.initialConfigData);
        }             
        
        console.log("Requesting intial config data from the api...");

        return this.apiClient.getLocations().then((result: ApiResult<LocationResultSet>) => {
            if(result.isSuccess()) {
                let locationResultSet = result.getBody();
                console.log("The total number of hotels : " + locationResultSet.hotels.length)
                let language = this.messageProvider.getCurrentLang();
                let initialConfigData:InitialConfigurationData = {
                    locations: this.sortByName(locationResultSet.towns, language)
                        .concat(this.sortByName(locationResultSet.attractions, language)
                        .concat(this.sortByName(locationResultSet.hotels, language))),
                    townships: locationResultSet.townships,
                    featuredLocations: locationResultSet.featuredLocations    
                }

                let initialConfigDataCacheObject:InitialConfigurationDataCacheObject = {
                    initialConfigData: initialConfigData,
                    timestamp: new Date().getTime()
                }
                
                this.localObjectCache.setObject("initial-config-data", initialConfigDataCacheObject, 60 * 60);
                return initialConfigData;
            } else {
                return {
                    locations: [],
                    townships: [],
                    featuredLocations: []
                }
            }
        });
    }

    getHotelAmenities(): Promise<Amenity[]>{
        return this.apiClient.getHotelAmenities();
    }

    getHotelsLookup(hotelLookupCriteria: HotelLookupCriteria): Promise<HotelResultSet>{
        return this.apiClient.getHotelsLookup(hotelLookupCriteria);
    }

    sortByName(locations: Location[], language:string){
        return locations.sort(function(firstLocation: Location, secondLocation: Location) {
            let firstLocationName:string = (language == 'en')? firstLocation.name.en : firstLocation.name.my;
            let secondLocationName:string = (language == 'en')? secondLocation.name.en : secondLocation.name.my;
            
            if (firstLocationName < secondLocationName) {
              return -1;
            }
            if (firstLocationName > secondLocationName) {
              return 1;
            }          
            // names must be equal
            return 0;
        });
    }

    searchHotels(searchCriteria:HotelSearchCriteria) : Promise<HotelResultSet> {
        return this.apiClient.searchHotels(searchCriteria);
    }

    async createNewBookingSession(channel:string, params:BookingSessionCreateParams): Promise<BookingSession>{
        console.log("Create booking session with params:", params);
        let bookingSession = await this.apiClient.createBookingSession(channel, params);
        return bookingSession;
    }

    async createBookingSession(channel:string, hotelId: number, searchCriteria:HotelSearchCriteria|RoomSearchCriteria): Promise<BookingSession>{
        let params:BookingSessionCreateParams = {
            hotelId: hotelId,
            checkInDate: searchCriteria.getCheckInDate(),
            checkOutDate: searchCriteria.getCheckOutDate(),
            numberOfRooms: searchCriteria.getNumberOfRooms(),
            numberOfGuests: 1,
            guestType: searchCriteria.getGuestType(),
        }
        console.log("Create booking session with params:", params);

        let bookingSession = await this.apiClient.createBookingSession(channel, params);
        return bookingSession;
    }

    async updateBookingSession(bookingSessionId: string, roomTypeSelections:TemporaryRoomTypeSelection[], removedRoomsIds:string[]): Promise<BookingSession> {  
        return await this.apiClient.updateBookingSession(bookingSessionId, roomTypeSelections, removedRoomsIds);
    }

    async getBookingSession(bookingSessionId: string): Promise<BookingSession> {
        return await this.apiClient.getBookingSession(bookingSessionId);
    }

    async getHotelDetails(hotelId: number, checkInDate?: string, checkOutDate?: string): Promise<HotelDetail> {
        return await this.apiClient.getHotelDetails(hotelId, checkInDate, checkOutDate);
    }

    async getRoomTypeResults(bookingSessionId:string) : Promise<RoomTypeResult[]> {
        return await this.apiClient.getRoomTypeResults(bookingSessionId);
    }

    async createTentativeBooking(
        channel: string|null,
        bookingSessionId: string, guest:GuestInfo, 
        totalAdults: number, totalChildren: number,
        specialRequest:string): Promise<ApiResult<TentativeBookingResult>> {  
        
        let booker:BookerInfo = {
            name: guest.name,
            emailAddress: guest.emailAddress,
            cellPhoneNumber: guest.cellPhoneNumber
        }

        return await this.apiClient.createTentativeBooking( channel,
            bookingSessionId, guest, booker, totalAdults, totalChildren, specialRequest);
    }

    async appliedProCodeTentativeBooking(channel:string, hotelId:number, tenativeBookingId:string, paymentOption: PaymentOption, promoCode:string){
        return await this.apiClient.verifyPromotion(channel, hotelId, tenativeBookingId, paymentOption, promoCode);
    }

    async payTentativeBooking(tentativeBookingId:string, channel:string){
        return await this.apiClient.payTentativeBookingNow(tentativeBookingId, channel);
    }

    async sentBookingLookupOTP(townId:number, checkInDate:string, checkOutDate:string, email:string, phoneNumber:string): Promise<ApiResult<BookingLookupOTP>> {  
        return await this.apiClient.sentBookingLookupOtp(townId, checkInDate, checkOutDate, email, phoneNumber);
    }

    async getBookingLookup(searchCriteria:BookingSearchCriteria|null, walletUserId:string|null): Promise<BookingDetail[]> {
        return await this.apiClient.getBookingLookup(searchCriteria, walletUserId);
    }

    async getHotelsByAgent(agentId: number): Promise<HotelDetailsByAgent> {
        return await this.apiClient.getHotelsByAgent(agentId);
    }

    async getHotelByAgent(agentId: number, hotelId:number): Promise<HotelDetailByAgent> {
        return await this.apiClient.getHotelByAgent(agentId, hotelId);
    }

    async createPaymentIntent(paymentToken: string): Promise<ApiResult<PaymentIntentCreateResponse>> {
            return await this.apiClient.createPaymentIntent(paymentToken);
        }
    
    async checkConfirmation(tenativeBookingId:string):Promise<CheckConfirmation> {
        return await this.apiClient.checkConfirmation(tenativeBookingId);
    }

    async checkPaymentProcessing(tenativeBookingId:string):Promise<PaymentConfirmation> {
        return await this.apiClient.checkPaymentProcessing(tenativeBookingId);
    }

    async getArticle(agentId: number, slug: string): Promise<Article> {
        return await this.apiClient.getArticle(agentId, slug);
    }

}